import { BRAND_NAME } from "@/brand";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { dashFetch } from "@/api/client";
import { useDashboard } from "@/context/DashboardContext";
import { getDashSocket } from "@/socket/dashSocket";
import { DASH_SOCKET_EVENTS } from "@/socket/events";
import type { DashUser } from "@/types/dashboard";
import DnsCopyBtn from "@/components/DnsCopyBtn";
import DnsInstructionsEmailModal from "@/components/DnsInstructionsEmailModal";
import { ADD_SITE_STEP, saveAddSiteWizardProgress } from "@/utils/addSiteWizardResume";
import { friendlyProvisionError } from "@/utils/provisionErrors";

type DomainVerification = {
  status?: string;
  token?: string;
  recordHost?: string;
  recordType?: string;
  recordValue?: string;
  proxyEdgeIpv4?: string[];
};

type VerificationStatus = {
  ok?: boolean;
  domain?: string;
  tenantStatus?: string;
  verification?: DomainVerification;
  txtVerified?: boolean;
  aRecordVerified?: boolean;
  error?: string;
  message?: string;
};

type AddSiteWizardProps = {
  open: boolean;
  
  onClose: () => void;
  onComplete: (domain: string) => void;
  onRegistered?: (domain: string) => void | Promise<void>;
  initialDomain?: string;
  initialStep?: number;
};

const STEPS = [
  { short: "Domain", title: "Enter your domain" },
  { short: "Verify", title: "Verify domain ownership" },
  { short: "Website", title: "Detect your website" },
  { short: "Content", title: "Discover pages" },
  { short: "DNS", title: "Point your A record" },
  { short: "Done", title: "Site activated" }
];

type SiteDetection = {
  generator?: string;
  label?: string;
  confidence?: string;
  signals?: string[];
};

type SettingsSkeletonRow = {
  id: string;
  name: string;
  description?: string;
};

type DiscoveryPage = {
  url: string;
  path: string;
  title: string;
  status: string;
  error?: string;
};

type DiscoveryStatus = {
  running?: boolean;
  phase?: string;
  message?: string;
  issue?: string;
  sitemapUrl?: string;
  done?: number;
  failed?: number;
  total?: number;
  pages?: DiscoveryPage[];
};

function discoveryCountLabel(st: DiscoveryStatus | null): string {
  const n = st?.done ?? 0;
  if (st?.phase === "sitemap" || st?.phase === "listing") return `${n} listed`;
  return `${n} page${n === 1 ? "" : "s"}`;
}

function discoveryRowLabel(p: DiscoveryPage): string {
  if (p.status === "failed" && p.error) return "Couldn't load";
  if (p.status === "done") return "In sitemap";
  if (p.status === "crawling") return "Checking…";
  return p.status;
}

function WordPressIcon() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="rgba(96,165,250,0.15)" stroke="rgba(96,165,250,0.45)" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="800" fill="rgba(255,255,255,0.92)">W</text>
    </svg>
  );
}

function isDashAdmin(user: DashUser): boolean {
  if (user.isAdministrator) return true;
  const role = String(user.role || "").toLowerCase();
  return role === "administrator" || role === "admin";
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 12l4 4L19 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpinnerIcon() {
  return <span className="s3WizSpinner" aria-hidden="true" />;
}

function shouldFallbackToRest(resp: unknown): boolean {
  if (!resp || typeof resp !== "object") return true;
  const r = resp as { ok?: boolean; error?: string; txtVerified?: boolean };
  if (r.ok === true && r.txtVerified === undefined && r.error === undefined) {
    return true;
  }
  if (r.ok !== false) return false;
  const err = String(r.error || "");
  return (
    err === "socket_not_connected" ||
    err === "timeout" ||
    err === "empty_response" ||
    err === "forbidden"
  );
}

async function apiCall<T>(
  dashRpc: ReturnType<typeof useDashboard>["dashRpc"],
  event: string,
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  try {
    const resp = await dashRpc<Record<string, unknown>, T>(event, body || {}, 60_000);
    if (resp && typeof resp === "object" && !shouldFallbackToRest(resp)) return resp;
  } catch { /* REST fallback */ }
  const method =
    path.includes("verification") || path.includes("/detect") || path.includes("discovery/status")
      ? "GET"
      : "POST";
  const url = method === "GET" && body?.domain
    ? `${path}${path.includes("?") ? "&" : "?"}domain=${encodeURIComponent(String(body.domain))}`
    : path;
  try {
    return await dashFetch<T>(url, {
      method,
      headers: method === "POST" ? { "content-type": "application/json" } : undefined,
      body: method === "POST" ? JSON.stringify(body || {}) : undefined
    });
  } catch (e) {
    throw new Error(friendlyProvisionError(e instanceof Error ? e.message : e));
  }
}

export default function AddSiteWizard({
  open,
  onClose,
  onComplete,
  onRegistered,
  initialDomain = "",
  initialStep = 0
}: AddSiteWizardProps) {
  const { dashRpc, loadTenants, setActiveSite, user } = useDashboard();
  const [step, setStep] = useState(initialStep);
  const [domain, setDomain] = useState(initialDomain);
  const [verification, setVerification] = useState<DomainVerification | null>(null);
  const [txtVerified, setTxtVerified] = useState(false);
  const [aRecordVerified, setARecordVerified] = useState(false);
  const [adminSkip, setAdminSkip] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgKind, setMsgKind] = useState<"" | "ok" | "bad" | "info">("");
  const [busy, setBusy] = useState(false);
  const [pollState, setPollState] = useState<"idle" | "running" | "success" | "error">("idle");
  const [verifyError, setVerifyError] = useState("");
  const [emailModalKind, setEmailModalKind] = useState<null | "txt" | "a">(null);
  const [detection, setDetection] = useState<SiteDetection | null>(null);
  const [skeletons, setSkeletons] = useState<SettingsSkeletonRow[]>([]);
  const [skeletonApplied, setSkeletonApplied] = useState(false);
  const [stagingUrl, setStagingUrl] = useState("");
  const [resolvedOriginIp, setResolvedOriginIp] = useState("");
  const [originUrl, setOriginUrl] = useState("");
  const [discovery, setDiscovery] = useState<DiscoveryStatus | null>(null);
  const [detectBusy, setDetectBusy] = useState(false);
  const [skeletonBusy, setSkeletonBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<number | null>(null);
  const discoveryPollRef = useRef<number | null>(null);
  const crawlListRef = useRef<HTMLDivElement>(null);

  const isAdmin = isDashAdmin(user);
  const progressPct = useMemo(() => ((step + 1) / STEPS.length) * 100, [step]);
  const dnsChecking = step === 1 && !txtVerified && pollState !== "error";

  const setStatus = useCallback((text: string, kind: "" | "ok" | "bad" | "info" = "") => {
    setMsg(text);
    setMsgKind(kind);
  }, []);

  const refreshVerification = useCallback(async (dom: string) => {
    const d = dom.trim();
    if (!d) return { ok: false as const, error: "invalid_domain" };
    try {
      const resp = await dashFetch<VerificationStatus>(
        `/dashboard/api/tenants/verification?domain=${encodeURIComponent(d)}`,
        { cache: "no-store", site: d }
      );
      if (resp?.ok) {
        const nextVerification = resp.verification || null;
        setVerification((prev) => {
          if (!prev && !nextVerification) return prev;
          if (
            prev &&
            nextVerification &&
            prev.recordHost === nextVerification.recordHost &&
            prev.recordValue === nextVerification.recordValue &&
            prev.status === nextVerification.status
          ) {
            return prev;
          }
          return nextVerification;
        });
        setTxtVerified((prev) => {
          const next = Boolean(resp.txtVerified);
          return prev === next ? prev : next;
        });
        setARecordVerified((prev) => {
          const next = Boolean(resp.aRecordVerified);
          return prev === next ? prev : next;
        });
        setVerifyError("");
      }
      return resp;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return { ok: false as const, error: msg };
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setDomain(initialDomain);
    setAdminSkip(false);
    setStatus("");
    setPollState(initialStep === 1 ? "running" : "idle");
    setVerifyError("");
    setDetection(null);
    setSkeletons([]);
    setSkeletonApplied(false);
    setStagingUrl("");
    setDiscovery(null);
    if (initialStep < 1) {
      setVerification(null);
      setTxtVerified(false);
      setARecordVerified(false);
    }
    const t = window.setTimeout(() => {
      if (initialStep === 0) inputRef.current?.focus();
    }, 40);
    return () => window.clearTimeout(t);
  }, [open, initialDomain, initialStep, setStatus]);

  useEffect(() => {
    if (!open || !initialDomain.trim() || initialStep < 1) return;
    void refreshVerification(initialDomain.trim()).then((resp) => {
      if (!resp?.ok) return;
      if (resp.verification) setVerification(resp.verification);
      setTxtVerified(Boolean(resp.txtVerified));
      setARecordVerified(Boolean(resp.aRecordVerified));
    });
  }, [open, initialDomain, initialStep, refreshVerification]);

  useEffect(() => {
    if (!open || step !== 1 || !domain.trim()) return;
    let cancelled = false;
    setPollState((s) => (s === "error" || s === "success" ? s : "running"));

    const poll = async () => {
      try {
        const resp = await refreshVerification(domain.trim());
        if (cancelled) return;
        if (!resp?.ok) {
          setVerifyError(
            friendlyProvisionError(resp) || "Could not check DNS. Try again or refresh the page."
          );
          setPollState("error");
          return;
        }
        setVerifyError("");
        if (resp.txtVerified) setPollState("success");
      } catch {
        if (!cancelled) setPollState("error");
      }
    };

    void poll();
    pollRef.current = window.setInterval(() => {
      void poll();
    }, 4000);
    return () => {
      cancelled = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [open, step, domain, refreshVerification]);

  useEffect(() => {
    if (!open || step !== 4 || !domain.trim()) return;
    const poll = async () => {
      try {
        const resp = await refreshVerification(domain.trim());
        if (resp?.aRecordVerified) setARecordVerified(true);
      } catch { /* ignore */ }
    };
    void poll();
    const id = window.setInterval(() => { void poll(); }, 5000);
    return () => window.clearInterval(id);
  }, [open, step, domain, refreshVerification]);

  const prepareSite = useCallback(async (dom: string) => {
    const resp = await apiCall<{
      ok?: boolean;
      stagingUrl?: string;
      origin?: string;
      resolvedIp?: string;
      error?: string;
      message?: string;
    }>(dashRpc, "tenants:prepare", "/dashboard/api/tenants/prepare", { domain: dom });
    if (resp?.ok) {
      if (resp.stagingUrl) setStagingUrl(String(resp.stagingUrl));
      if (resp.resolvedIp) setResolvedOriginIp(String(resp.resolvedIp));
      if (resp.origin) setOriginUrl(String(resp.origin));
    }
    return resp;
  }, [dashRpc]);

  const runDetection = useCallback(async (dom: string) => {
    setDetectBusy(true);
    try {
      const resp = await apiCall<{
        ok?: boolean;
        detection?: SiteDetection;
        skeletons?: SettingsSkeletonRow[];
      }>(dashRpc, "tenants:detect", `/dashboard/api/tenants/detect?domain=${encodeURIComponent(dom)}`);
      if (resp?.ok) {
        setDetection(resp.detection || null);
        setSkeletons(Array.isArray(resp.skeletons) ? resp.skeletons : []);
      }
    } finally {
      setDetectBusy(false);
    }
  }, [dashRpc]);

  const applyDefaultWpSkeleton = useCallback(async () => {
    const dom = domain.trim();
    if (!dom) return;
    setSkeletonBusy(true);
    setStatus("Applying WordPress settings…", "info");
    try {
      const resp = await apiCall<{ ok?: boolean; skeletonName?: string; error?: string; message?: string }>(
        dashRpc,
        "tenants:apply-skeleton",
        "/dashboard/api/tenants/apply-skeleton",
        { domain: dom, skeletonId: "default-wordpress" }
      );
      if (!resp?.ok) {
        setStatus(friendlyProvisionError(resp), "bad");
        return;
      }
      setSkeletonApplied(true);
      setStatus(`Applied ${resp.skeletonName || "Default WordPress settings"}.`, "ok");
    } catch (e) {
      setStatus(friendlyProvisionError(e instanceof Error ? e.message : e), "bad");
    } finally {
      setSkeletonBusy(false);
    }
  }, [domain, dashRpc, setStatus]);

  const refreshDiscovery = useCallback(async (dom: string) => {
    const resp = await apiCall<{ ok?: boolean; status?: DiscoveryStatus }>(
      dashRpc,
      "tenants:discovery:status",
      `/dashboard/api/tenants/discovery/status?domain=${encodeURIComponent(dom)}`
    );
    if (resp?.ok && resp.status) setDiscovery(resp.status);
  }, [dashRpc]);

  const startDiscovery = useCallback(async (dom: string) => {
    const resp = await apiCall<{ ok?: boolean; status?: DiscoveryStatus }>(
      dashRpc,
      "tenants:discovery:start",
      "/dashboard/api/tenants/discovery/start",
      { domain: dom, maxPages: 500, sitemapOnly: true }
    );
    if (resp?.ok && resp.status) setDiscovery(resp.status);
  }, [dashRpc]);

  useEffect(() => {
    if (!open || step !== 2 || !domain.trim()) return;
    void (async () => {
      setStatus("Connecting to your site…", "info");
      const prep = await prepareSite(domain.trim());
      if (!prep?.ok) {
        setStatus(friendlyProvisionError(prep), "bad");
        return;
      }
      setStatus("");
      await runDetection(domain.trim());
    })();
  }, [open, step, domain, prepareSite, runDetection, setStatus]);

  useEffect(() => {
    if (!open || step !== 3 || !domain.trim()) return;
    const dom = domain.trim();
    void startDiscovery(dom).then(() => refreshDiscovery(dom));

    const socket = getDashSocket();
    const onProgress = (payload: { domain?: string; status?: DiscoveryStatus }) => {
      const d = String(payload?.domain || "").toLowerCase();
      if (d && d !== dom.toLowerCase() && d !== `www.${dom.toLowerCase()}`) return;
      if (payload?.status) setDiscovery(payload.status);
    };
    socket?.on(DASH_SOCKET_EVENTS.discoveryProgress, onProgress);

    discoveryPollRef.current = window.setInterval(() => {
      void refreshDiscovery(dom);
    }, 600);
    return () => {
      socket?.off(DASH_SOCKET_EVENTS.discoveryProgress, onProgress);
      if (discoveryPollRef.current) window.clearInterval(discoveryPollRef.current);
    };
  }, [open, step, domain, startDiscovery, refreshDiscovery]);

  useEffect(() => {
    if (crawlListRef.current) crawlListRef.current.scrollTop = 0;
  }, [discovery?.pages?.length]);

  const registerDomain = useCallback(async () => {
    const domainRaw = domain.trim();
    if (!domainRaw) return;
    setBusy(true);
    setStatus("Registering site…", "info");
    try {
      const resp = await apiCall<{
        ok?: boolean;
        tenant?: { domain?: string };
        verification?: DomainVerification;
        message?: string;
        error?: string;
      }>(dashRpc, "tenants:register", "/dashboard/api/tenants/register", { domain: domainRaw });
      if (!resp?.ok) {
        setStatus(friendlyProvisionError(resp), "bad");
        return;
      }
      const newDomain = String(resp.tenant?.domain || domainRaw);
      setDomain(newDomain);
      setVerification(resp.verification || null);
      await loadTenants();
      await onRegistered?.(newDomain);
      await setActiveSite(newDomain);
      setStep(1);
      setStatus("Site registered. Add the DNS record below to verify ownership.", "info");
    } catch (e) {
      setStatus(friendlyProvisionError(e instanceof Error ? e.message : e), "bad");
    } finally {
      setBusy(false);
    }
  }, [domain, dashRpc, loadTenants, onRegistered, setActiveSite, setStatus]);

  const confirmVerification = useCallback(async () => {
    const domainRaw = domain.trim();
    if (!domainRaw) return;
    setBusy(true);
    setStatus(adminSkip ? "Marking as verified…" : "Checking DNS…", "info");
    try {
      const resp = await dashFetch<VerificationStatus>("/dashboard/api/tenants/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        site: domainRaw,
        body: JSON.stringify({ domain: domainRaw, skipVerification: adminSkip })
      });
      if (!resp?.ok) {
        setStatus(friendlyProvisionError(resp), "bad");
        return;
      }
      setTxtVerified(true);
      setVerification(resp.verification || verification);
      setStep(2);
      setStatus("Ownership verified. Next we will inspect your website.", "ok");
    } catch (e) {
      setStatus(friendlyProvisionError(e instanceof Error ? e.message : e), "bad");
    } finally {
      setBusy(false);
    }
  }, [adminSkip, domain, dashRpc, setStatus, verification]);

  const activateSite = useCallback(async () => {
    const domainRaw = domain.trim();
    if (!domainRaw) return;
    setBusy(true);
    setStatus("Activating site…", "info");
    try {
      const resp = await apiCall<{
        ok?: boolean;
        tenant?: { domain?: string };
        message?: string;
        error?: string;
      }>(dashRpc, "tenants:activate", "/dashboard/api/tenants/activate", {
        domain: domainRaw,
        skipVerification: adminSkip
      });
      if (!resp?.ok) {
        setStatus(friendlyProvisionError(resp), "bad");
        return;
      }
      await loadTenants();
      void saveAddSiteWizardProgress(domainRaw, ADD_SITE_STEP.done);
      setStep(ADD_SITE_STEP.done);
      setStatus("Site is live.", "ok");
      onComplete(domainRaw);
    } catch (e) {
      setStatus(friendlyProvisionError(e instanceof Error ? e.message : e), "bad");
    } finally {
      setBusy(false);
    }
  }, [adminSkip, domain, dashRpc, loadTenants, onComplete, setStatus]);

  useEffect(() => {
    if (step === 4 && aRecordVerified && !busy) {
      void activateSite();
    }
  }, [step, aRecordVerified, busy, activateSite]);

  const persistWizardStep = useCallback((nextStep: number, preview?: boolean) => {
    const d = domain.trim();
    if (!d) return;
    void saveAddSiteWizardProgress(
      d,
      nextStep,
      preview ?? nextStep >= ADD_SITE_STEP.content
    );
  }, [domain]);

  const handleNext = () => {
    if (step === 0) void registerDomain();
    else if (step === 1) void confirmVerification();
    else if (step === 2) {
      persistWizardStep(ADD_SITE_STEP.content, true);
      setStep(ADD_SITE_STEP.content);
    } else if (step === 3) {
      persistWizardStep(ADD_SITE_STEP.dns);
      setStep(ADD_SITE_STEP.dns);
    } else if (step === 4) void activateSite();
    else onClose();
  };

  useEffect(() => {
    if (!open || !domain.trim()) return;
    persistWizardStep(step, step >= ADD_SITE_STEP.content);
  }, [open, domain, step, persistWizardStep]);

  const handleBack = () => {
    if (step <= 0 || busy) return;
    setStep((s) => Math.max(0, s - 1));
    setStatus("");
  };

  const proxyIps = verification?.proxyEdgeIpv4?.filter(Boolean) || [];
  const isWordPress = detection?.generator === "wordpress";
  const discoveryRunning = Boolean(discovery?.running);
  const canNext = step === 0 ? Boolean(domain.trim()) && !busy
    : step === 1 ? (txtVerified || adminSkip) && !busy
      : step === 2 ? !busy && !detectBusy
        : step === 3 ? !busy
          : step === 4 ? !busy
            : true;

  const nextLabel = step === 0 ? "Continue"
    : step === 1 ? (txtVerified ? "Continue" : "Check DNS")
      : step === 2 ? "Continue"
        : step === 3 ? (discoveryRunning ? "Continue anyway" : "Continue to DNS")
          : step === 4 ? (aRecordVerified ? "Activating…" : "Activate")
            : "Close";

  if (!open) return null;

  return createPortal(
    <div
      id="addSiteWizardModal"
      className="modal open"
      aria-hidden="false"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="modalCard s3WizCard" role="dialog" aria-modal="true" aria-label="Add website wizard">
        <div className="modalHeader s3WizHeader">
          <div className="s3WizHeaderTop">
            <div className="modalTitle">
              <strong>Add website</strong>
            </div>
            <button type="button" className="iconBtn" title="Close" disabled={busy} onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div className="s3WizProgressTrack" aria-hidden="true">
            <div className="s3WizProgressFill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div className="s3WizStepper" aria-label="Wizard progress">
          {STEPS.map((s, i) => (
            <div
              key={s.short}
              className={"s3WizStep" + (i === step ? " is-active" : i < step ? " is-done" : "")}
            >
              <span className="s3WizStepNum">{i < step ? <CheckIcon /> : i + 1}</span>
              <span className="s3WizStepLabel">{s.short}</span>
            </div>
          ))}
        </div>

        <div className="modalBody s3WizBody">
          <div className="s3WizStepPanel">
            <h4 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 800 }}>{STEPS[step]?.title}</h4>

            {step === 0 && (
              <>
                <p className="s3WizLead">
                  Enter the domain you want to add. We&apos;ll register it immediately, but it won&apos;t go live until ownership is verified.
                </p>
                <div className="s3WizField">
                  <div className="s3WizFieldLabel">Domain name</div>
                  <input
                    ref={inputRef}
                    type="text"
                    className="settingsInput settingsMono siteModalInput"
                    placeholder="example.com"
                    autoComplete="off"
                    spellCheck={false}
                    value={domain}
                    disabled={busy}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") void handleNext(); }}
                  />
                  <div className="s3WizFieldHelp">Use the bare domain without <span className="mono">https://</span> or paths.</div>
                </div>
              </>
            )}

            {step === 1 && verification && (
              <>
                <p className="s3WizLead">
                  Add this TXT record at your DNS provider to prove you control <span className="mono">{domain}</span>.
                  We check automatically every few seconds.
                </p>
                <div className="s3WizDnsBox">
                  <p className="s3WizDnsTitle">DNS record</p>
                  <ol className="s3WizDnsSteps">
                    <li>Open your DNS provider for <span className="mono">{domain}</span>.</li>
                    <li>Add a <strong>TXT</strong> record with the values below.</li>
                    <li>Save and wait for propagation (usually a few minutes).</li>
                  </ol>
                  <div className="s3WizDnsIdRow">
                    <span className="muted">Host / name</span>
                    <code className="settingsMono s3WizDnsValue">{verification.recordHost}</code>
                    <DnsCopyBtn value={String(verification.recordHost || "")} />
                  </div>
                  <div className="s3WizDnsIdRow">
                    <span className="muted">Type</span>
                    <code className="settingsMono s3WizDnsValue">{verification.recordType || "TXT"}</code>
                  </div>
                  <div className="s3WizDnsIdRow">
                    <span className="muted">Value</span>
                    <code className="settingsMono s3WizDnsValue">{verification.recordValue}</code>
                    <DnsCopyBtn value={String(verification.recordValue || "")} />
                  </div>
                  <div className="s3WizDnsActions">
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => setEmailModalKind("txt")}
                    >
                      Send instructions in email
                    </button>
                  </div>
                </div>

                <DnsInstructionsEmailModal
                  open={emailModalKind === "txt"}
                  kind="txt"
                  domain={domain}
                  recordHost={String(verification.recordHost || "")}
                  recordType={String(verification.recordType || "TXT")}
                  recordValue={String(verification.recordValue || "")}
                  defaultEmail={String(user.email || "")}
                  onClose={() => setEmailModalKind(null)}
                  onSent={(to) => setStatus(`Instructions sent to ${to}.`, "ok")}
                />

                <div
                  className={
                    "s3WizTestCard" +
                    (txtVerified
                      ? " s3WizTestCard--success"
                      : pollState === "error"
                        ? " s3WizTestCard--error"
                        : dnsChecking
                          ? " s3WizTestCard--running"
                          : "")
                  }
                >
                  <div
                    className={
                      "s3WizTestIcon" +
                      (txtVerified
                        ? " s3WizTestIcon--success"
                        : pollState === "error"
                          ? " s3WizTestIcon--error"
                          : dnsChecking
                            ? " s3WizTestIcon--running"
                            : "")
                    }
                  >
                    {txtVerified ? (
                      <CheckIcon />
                    ) : pollState === "error" ? (
                      <span className="s3WizFailMark" aria-hidden="true">
                        !
                      </span>
                    ) : (
                      <SpinnerIcon />
                    )}
                  </div>
                  <div className="s3WizTestCopy">
                    <div className="s3WizTestTitle">
                      {txtVerified
                        ? "Ownership verified"
                        : pollState === "error"
                          ? "DNS check failed"
                          : "Checking DNS…"}
                    </div>
                    <div className="s3WizTestMsg">
                      {txtVerified
                        ? "TXT record found. Continue to the next step."
                        : pollState === "error" && verifyError
                          ? verifyError
                          : "Waiting for TXT record at " + (verification.recordHost || "_sitewrap-verify")}
                    </div>
                  </div>
                </div>

                {isAdmin && (
                  <label className="s3WizCheckRow">
                    <input
                      type="checkbox"
                      checked={adminSkip}
                      onChange={(e) => setAdminSkip(e.target.checked)}
                    />
                    <span>Mark as verified (admin skip)</span>
                  </label>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <p className="s3WizLead">
                  We looked up the <strong>A record</strong> for <span className="mono">{domain}</span>
                  {resolvedOriginIp ? (
                    <> (currently <span className="mono">{resolvedOriginIp}</span>)</>
                  ) : null}
                  , set that as the origin server
                  {originUrl ? <> (<span className="mono">{originUrl}</span>)</> : null}
                  , then fetched the homepage to detect the CMS.
                  Apply a settings skeleton to pre-configure protection before you go live.
                </p>
                <div className="wizDetectCard">
                  {detectBusy ? (
                    <div className="s3WizTestCard s3WizTestCard--running">
                      <div className="s3WizTestIcon s3WizTestIcon--running"><SpinnerIcon /></div>
                      <div className="s3WizTestCopy">
                        <div className="s3WizTestTitle">Analyzing homepage…</div>
                        <div className="s3WizTestMsg">Checking HTML and common CMS fingerprints.</div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="wizDetectHero">
                        {isWordPress ? <WordPressIcon /> : (
                          <span className="wizDetectGenericIcon" aria-hidden="true">?</span>
                        )}
                        <div>
                          <div className="wizDetectLabel">{detection?.label || "Unknown"}</div>
                          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                            Confidence: {detection?.confidence || "—"}
                            {detection?.signals?.length ? ` · ${detection.signals.slice(0, 2).join(" · ")}` : ""}
                          </div>
                        </div>
                      </div>
                      {isWordPress ? (
                        <div className="wizDetectActions">
                          <button
                            type="button"
                            className="primaryBtn"
                            disabled={skeletonBusy || skeletonApplied}
                            onClick={() => void applyDefaultWpSkeleton()}
                          >
                            {skeletonApplied ? "Default WordPress settings applied" : "Apply default WordPress settings"}
                          </button>
                          <p className="muted" style={{ margin: "10px 0 0", fontSize: 12, lineHeight: 1.45 }}>
                            Gates wp-admin, enables WAF rules, hides WordPress paths, and sets cache exclusions for admin routes.
                          </p>
                        </div>
                      ) : (
                        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                          No WordPress-specific skeleton matched. You can still continue and configure settings later.
                        </p>
                      )}
                      {skeletons.length > 1 ? (
                        <p className="muted mono" style={{ fontSize: 11, marginTop: 12 }}>
                          {skeletons.length} skeleton(s) available for this generator.
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p className="s3WizLead">
                  We read your sitemap and list every URL here — no page fetching during this step. Full preload runs in the
                  background using your Preload delay (typically 2000&nbsp;ms between requests).
                </p>
                {discovery?.issue && (discovery.phase === "error" || discovery.phase === "done") ? (
                  <div className="s3WizNotice s3WizNotice--warn" role="status" style={{ marginBottom: 12 }}>
                    {discovery.issue}
                  </div>
                ) : null}
                <div className="wizCrawlPanel">
                  <div className="wizCrawlHead">
                    <div>
                      <div className="wizCrawlPhase">
                        {discovery?.running ? (
                          <SpinnerIcon />
                        ) : discovery?.phase === "done" ? (
                          <CheckIcon />
                        ) : discovery?.phase === "error" ? null : null}
                        <span>{discovery?.message || "Starting discovery…"}</span>
                      </div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                        {discovery?.sitemapUrl ? (
                          <>
                            Sitemap: <span className="mono">{discovery.sitemapUrl}</span>
                            {" · "}
                          </>
                        ) : discovery?.running ? (
                          "Searching for sitemap… · "
                        ) : null}
                        {discoveryCountLabel(discovery)}
                        {(discovery?.failed ?? 0) > 0 ? ` · ${discovery?.failed} couldn't be read` : ""}
                      </div>
                    </div>
                    {stagingUrl ? (
                      <a className="btn" href={stagingUrl} target="_blank" rel="noopener noreferrer">
                        Open staging site
                      </a>
                    ) : null}
                  </div>
                  <div className="wizCrawlList" ref={crawlListRef}>
                    {(discovery?.pages || []).length === 0 ? (
                      <div className="wizCrawlEmpty muted">
                        {discovery?.running
                          ? "Pages will appear here as soon as we parse your sitemap…"
                          : "No pages listed yet."}
                      </div>
                    ) : (
                      (discovery?.pages || []).map((p) => (
                        <div key={p.url} className={"wizCrawlRow wizCrawlRow--" + p.status}>
                          <span className={"wizCrawlDot wizCrawlDot--" + p.status} aria-hidden="true" />
                          <div className="wizCrawlRowBody">
                            <div className="wizCrawlTitle">{p.title || p.path}</div>
                            <div className="wizCrawlUrl mono">{p.path}</div>
                            {p.status === "failed" && p.error ? (
                              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                                {p.error}
                              </div>
                            ) : null}
                          </div>
                          <span className="wizCrawlStatus muted">{discoveryRowLabel(p)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {step === 4 && (
              <>
                <p className="s3WizLead">
                  Point your domain&apos;s <strong>A record</strong> to this server. Once DNS propagates, we&apos;ll activate the site automatically.
                </p>
                <div className="s3WizDnsBox">
                  <p className="s3WizDnsTitle">A record</p>
                  <ol className="s3WizDnsSteps">
                    <li>In your DNS panel, set the root <span className="mono">@</span> or <span className="mono">{domain}</span> A record.</li>
                    <li>Use one of the server IP addresses below.</li>
                    <li>Remove or update any conflicting A records for this domain.</li>
                  </ol>
                  {proxyIps.length ? proxyIps.map((ip) => (
                    <div key={ip} className="s3WizDnsIdRow">
                      <span className="muted">A record →</span>
                      <code className="settingsMono s3WizDnsValue">{ip}</code>
                      <DnsCopyBtn value={ip} />
                    </div>
                  )) : (
                    <div className="s3WizDnsIdRow">
                      <span className="muted">Server IP</span>
                      <code className="settingsMono">Loading…</code>
                    </div>
                  )}
                  <div className="s3WizDnsActions">
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => setEmailModalKind("a")}
                    >
                      Send instructions in email
                    </button>
                  </div>
                </div>

                <DnsInstructionsEmailModal
                  open={emailModalKind === "a"}
                  kind="a"
                  domain={domain}
                  proxyIps={proxyIps}
                  defaultEmail={String(user.email || "")}
                  onClose={() => setEmailModalKind(null)}
                  onSent={(to) => setStatus(`Instructions sent to ${to}.`, "ok")}
                />

                <div className={"s3WizTestCard" + (aRecordVerified ? " s3WizTestCard--success" : " s3WizTestCard--running")}>
                  <div className={"s3WizTestIcon" + (aRecordVerified ? " s3WizTestIcon--success" : " s3WizTestIcon--running")}>
                    {aRecordVerified ? <CheckIcon /> : <SpinnerIcon />}
                  </div>
                  <div className="s3WizTestCopy">
                    <div className="s3WizTestTitle">{aRecordVerified ? "A record detected" : "Waiting for A record…"}</div>
                    <div className="s3WizTestMsg">
                      {aRecordVerified
                        ? "DNS points to this server. Activating…"
                        : `Set A record for ${domain} to ${proxyIps.join(" or ") || "the server IP"}`}
                    </div>
                  </div>
                </div>
              </>
            )}

            {step === 5 && (
              <div className="s3WizSummary">
                <div className="s3WizSummaryTitle">Site activated</div>
                <ul>
                  <li><span className="mono">{domain}</span> is now live on {BRAND_NAME}.</li>
                  <li>Preloading and edge configuration have started.</li>
                  <li>You can manage the site from Settings anytime.</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="s3WizFoot">
          {step > 0 && step < 5 ? (
            <button type="button" className="btn" disabled={busy} onClick={handleBack}>Back</button>
          ) : (
            <span />
          )}
          <span className={"s3WizMsg" + (msgKind ? ` s3WizMsg--${msgKind === "bad" ? "bad" : msgKind === "ok" ? "ok" : "info"}` : "")}>
            {msg || "—"}
          </span>
          <button
            type="button"
            className={"primaryBtn" + (!canNext ? " s3WizNextBtn--blocked" : "")}
            disabled={!canNext}
            onClick={handleNext}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
